import { Worksheet } from '../../../types/worksheet';
import { formatDate } from '../../../utilities/formatting';
import CalculationCard from './CalculationCard';
import { useWorkpaper } from '../../../queries/use-workpaper';
import { useMemo } from 'react';
import { useCalculations } from '../../../queries/use-calculations';
import { CWTBehaviourEnum } from '../../../types/custom-worksheet-v2';
import { AlertV2, Spinner } from '@cimplico/components';

type PriorCalculationsProps = {
    priorWorksheet: Worksheet;
    currentWorksheet: Worksheet;
};

function PriorCalculations({ priorWorksheet, currentWorksheet }: PriorCalculationsProps): JSX.Element {
    const priorWorkpaperQuery = useWorkpaper(priorWorksheet.workpaperId);
    const priorWorkpaper = useMemo(() => priorWorkpaperQuery.data?.workpaper, [priorWorkpaperQuery.data]);

    const currentCalculationsQuery = useCalculations({ allowAsSchedule: true });
    const currentCalculations = useMemo(() => currentCalculationsQuery.data?.calculations, [currentCalculationsQuery.data]) ?? [];

    const priorCalculationsQuery = useCalculations({ allowAsSchedule: true }, priorWorkpaper?.id);
    const priorCalculations = useMemo(() => priorCalculationsQuery.data?.calculations, [priorCalculationsQuery.data]) ?? [];

    // tracks whether calculations (that are shared and have not been carried forward) have already been added from a template in the current
    const existingCalcOnWp = useMemo(() => {
        const toTrack = new Set(
            currentCalculations
                .filter((c) => c.behaviour === CWTBehaviourEnum.SHARE && c.priorCalculationId === null)
                .map((c) => c.templateId)
        );

        return new Set(priorCalculations.filter((p) => toTrack.has(p.templateId)).map((p) => p.id));
    }, [priorCalculations, currentCalculations]);

    // tracks whether calculations have already been carried forward to this worksheet
    const existingCalcOnWs = useMemo(
        () => new Set(currentWorksheet.calculations.map((c) => c.priorCalculationId).filter((id) => !!id)),
        [currentWorksheet.calculations]
    );

    if (priorCalculationsQuery.isLoading || currentCalculationsQuery.isLoading || priorCalculationsQuery.isLoading) return <></>;
    const isError = priorCalculationsQuery.isError || currentCalculationsQuery.isError || priorCalculationsQuery.isError;

    return (
        <div className="mb-10 mt-10" data-intercom-target="cf-prior-calculations">
            <div className="flex items-center mb-8">
                <div className="flex-grow h-px bg-gray-300"></div>
                <span className="mx-2 text-xs text-gray-500">{`${
                    priorWorkpaper?.periodEnd ? `${formatDate(priorWorkpaper?.periodEnd)}` : 'Previous Period'
                }`}</span>
                <div className="flex-grow h-px bg-gray-300"></div>
            </div>
            {isError ? (
                <AlertV2
                    type={'error'}
                    title={'Error'}
                    description={'Failed to retrieve calculations linked in prior period. Please try again.'}
                />
            ) : (
                <div
                    className="grid gap-4 grid-cols-[repeat(auto-fill,300px)] mt-4"
                    data-intercom-target="cf-prior-calculations-calculation-card"
                >
                    {priorWorksheet.calculations.map((calc) => (
                        <CalculationCard
                            calculation={calc}
                            key={calc.id}
                            worksheetId={priorWorksheet.id}
                            priorDetails={{
                                priorWorkpaperId: priorWorksheet.workpaperId,
                                wsHasAccount: !!priorWorksheet.account,
                                disableCarryForward: existingCalcOnWp.has(calc.id) || existingCalcOnWs.has(calc.id),
                                disableCarryForwardHint: existingCalcOnWp.has(calc.id)
                                    ? 'Calculation has already been created from a template in the current workpaper.'
                                    : existingCalcOnWs.has(calc.id)
                                    ? 'Calculation has already been carried forward to this worksheet.'
                                    : undefined,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default PriorCalculations;
